/**
 * CSV Utilities for Student Batch Import
 * Ported from backend/utils/dataImport.js (stripped of Node.js fs deps)
 */
import { saveFile } from './saveFile';

// Flexible column name mapping to student field names
const CSV_COLUMN_MAP = {
  'firstname': 'firstName',
  'first name': 'firstName',
  'first_name': 'firstName',
  'שם פרטי': 'firstName',
  'الاسم الأول': 'firstName',
  'lastname': 'lastName',
  'last name': 'lastName',
  'last_name': 'lastName',
  'שם משפחה': 'lastName',
  'اسم العائلة': 'lastName',
  'phone': 'phone',
  'טלפון': 'phone',
  'هاتف': 'phone',
  'email': 'email',
  'דוא"ל': 'email',
  'بريد إلكتروني': 'email',
  'nationalid': 'nationalId',
  'national id': 'nationalId',
  'national_id': 'nationalId',
  'ת.ז.': 'nationalId',
  'رقم الهوية': 'nationalId',
  'gender': 'gender',
  'מין': 'gender',
  'جنس': 'gender',
  'dateofbirth': 'dateOfBirth',
  'date of birth': 'dateOfBirth',
  'date_of_birth': 'dateOfBirth',
  'dob': 'dateOfBirth',
  'תאריך לידה': 'dateOfBirth',
  'تاريخ الميلاد': 'dateOfBirth',
  'parentphone': 'parentPhone',
  'parent phone': 'parentPhone',
  'parent_phone': 'parentPhone',
  'טלפון הורה': 'parentPhone',
  'هاتف ولي الأمر': 'parentPhone',
  'city': 'city',
  'עיר': 'city',
  'مدينة': 'city',
  'address': 'address',
  'כתובת': 'address',
  'عنوان': 'address',
  'guardianname': 'guardianName',
  'guardian name': 'guardianName',
  'guardian_name': 'guardianName',
  'שם אפוטרופוס': 'guardianName',
  'اسم ولي الأمر': 'guardianName',
  'guardianrelationship': 'guardianRelationship',
  'guardian relationship': 'guardianRelationship',
  'guardian_relationship': 'guardianRelationship',
  'guardianmobile': 'guardianMobile',
  'guardian mobile': 'guardianMobile',
  'guardian_mobile': 'guardianMobile',
  'guardianid': 'guardianId',
  'guardian id': 'guardianId',
  'guardian_id': 'guardianId',
};

/**
 * Parse a single CSV line handling quoted fields and escaped quotes
 */
function parseCSVLine(line) {
  const result = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (inQuotes) {
      if (char === '"' && nextChar === '"') {
        current += '"';
        i++; // skip escaped quote
      } else if (char === '"') {
        inQuotes = false;
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
  }
  result.push(current.trim());
  return result;
}

/**
 * Parse CSV text into structured student data
 * @param {string} csvText - raw CSV content
 * @returns {{ headers: string[], mappedHeaders: string[], rows: object[], rawRows: string[][] }}
 */
export function parseStudentCSV(csvText) {
  const lines = csvText.split(/\r?\n/).filter(line => line.trim());

  if (lines.length < 2) {
    throw new Error('CSV must have a header row and at least one data row');
  }

  const rawHeaders = parseCSVLine(lines[0]);
  const mappedHeaders = rawHeaders.map(h => {
    const normalized = h.toLowerCase().trim();
    return CSV_COLUMN_MAP[normalized] || normalized;
  });

  const rawRows = [];
  const rows = [];

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    if (values.every(v => !v)) continue; // skip empty rows

    rawRows.push(values);
    const row = {};
    mappedHeaders.forEach((header, idx) => {
      if (values[idx] !== undefined && values[idx] !== '') {
        row[header] = values[idx];
      }
    });
    rows.push(row);
  }

  return { headers: rawHeaders, mappedHeaders, rows, rawRows };
}

/**
 * Validate a single student row
 * @param {object} row - parsed student data
 * @param {number} index - row index (0-based)
 * @returns {string[]} array of error messages (empty if valid)
 */
export function validateStudentRow(row, index) {
  const errors = [];
  if (!row.firstName || row.firstName.trim().length < 2) {
    errors.push(`firstName is required (min 2 chars)`);
  }
  if (!row.lastName || row.lastName.trim().length < 2) {
    errors.push(`lastName is required (min 2 chars)`);
  }
  if (row.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) {
    errors.push(`Invalid email format`);
  }
  return errors;
}

/**
 * Generate a template CSV string for download
 * @returns {string} CSV content
 */
export function generateTemplateCSV() {
  const headers = [
    'firstName', 'lastName', 'phone', 'email', 'nationalId',
    'gender', 'dateOfBirth', 'parentPhone', 'city', 'address',
    'guardianName', 'guardianRelationship', 'guardianMobile', 'guardianId'
  ];
  const exampleRow = [
    'Ahmed', 'Hassan', '0521234567', 'ahmed@example.com', '123456789',
    'male', '2005-03-15', '0509876543', 'Haifa', '123 Main St',
    'Fatima Hassan', 'mother', '0509876543', ''
  ];
  return headers.join(',') + '\n' + exampleRow.join(',') + '\n';
}

// Export columns. Header labels are chosen so StudentImportModal's
// autoMapHeaders maps every one back to its field — an exported file can be
// edited and re-imported without touching the column mapping.
export const STUDENT_EXPORT_COLUMNS = [
  ['firstName', 'First name'],
  ['middleName', 'Middle name'],
  ['lastName', 'Last name'],
  ['phone', 'Phone'],
  ['parentPhone', 'Parent phone'],
  ['email', 'Email'],
  ['nationalId', 'National ID'],
  ['gender', 'Gender'],
  ['dateOfBirth', 'Date of birth'],
  ['city', 'City'],
  ['address', 'Address'],
];

function escapeCSVCell(value) {
  // Newlines are flattened: the importer's CSV parser is line-based, so a
  // quoted multi-line cell would not survive a round trip.
  const s = value == null ? '' : String(value).replace(/\r?\n|\r/g, ' ');
  return /[",]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/**
 * Build a re-importable CSV of students.
 * @param {object[]} students
 * @returns {string} CSV content (no BOM — downloadCSV adds it)
 */
export function buildStudentsExportCSV(students) {
  const lines = [STUDENT_EXPORT_COLUMNS.map(([, label]) => label).join(',')];
  for (const s of students || []) {
    lines.push(STUDENT_EXPORT_COLUMNS.map(([field]) => {
      // Older records keep the parent's number under guardianMobile only.
      const value = field === 'parentPhone' ? (s.parentPhone || s.guardianMobile) : s[field];
      return escapeCSVCell(value);
    }).join(','));
  }
  return lines.join('\n') + '\n';
}

/**
 * Download a CSV string as a file
 * @param {string} csvContent
 * @param {string} filename
 */
export function downloadCSV(csvContent, filename = 'template.csv') {
  // Add BOM for Excel UTF-8 compatibility
  const bom = '\uFEFF';
  const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });
  // saveFile = anchor download on web (unchanged), Filesystem+Share on native.
  return saveFile(blob, filename, 'text/csv');
}
